/**
 * O renderer da tela — a entrada pública do motor.
 *
 * O que NÃO existe aqui, e é o ponto principal da Fase 3:
 * zero `if (viewMode === 'edit')`, zero overlay, zero `document.querySelector`,
 * zero `setTimeout`, zero `MutationObserver`, zero forçamento de cor por
 * JavaScript, zero varredura de `__ngContext__`. Compare com o
 * `canvas.component.ts` (2.556 linhas), que faz os dois papéis ao mesmo tempo.
 *
 * O editor não é um modo deste componente: é um serviço opcional que o shell
 * provê (`EDITOR_CHROME`). Em produção o token não está provido, e o custo da
 * edição desaparece junto com ele.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';

import { ScreenNode } from '../model/node-props';
import { ScreenDocument } from '../model/screen-schema';
import { NodeHostComponent } from './node-host.component';
import { RenderMode } from './node-renderer';
import { ScreenContext } from './screen-context';
import { DataBindingService } from '../services/data-binding.service';
import { DataSyncService } from '../services/data-sync.service';
import { RxTimingService } from '../services/rx-timing.service';

/** Uma mudança de valor saindo da tela. */
export interface ScreenValueChange {
  /** Id do nó que mudou. */
  readonly nodeId: string;
  /** Nome do campo, quando o nó tem `binding.fieldName`. */
  readonly fieldName?: string;
  readonly value: unknown;
}

@Component({
  selector: 'tem-screen-render',
  standalone: true,
  imports: [CommonModule, NodeHostComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Uma instancia POR TELA: `simple-datagrid` mantem grade pai e filha vivas ao
  // mesmo tempo, e duas telas compartilhando contexto se corromperiam.
  providers: [
    ScreenContext,
    // Os tres sao `@Injectable()` sem `providedIn`, e a grade os injeta. POR
    // TELA, e nao em root: e o que isola grade pai e grade filha no
    // `simple-datagrid`, que abre as duas ao mesmo tempo — em root, as duas
    // compartilhariam o mesmo estado de dados e uma sobrescreveria a outra.
    DataBindingService,
    DataSyncService,
    RxTimingService
  ],
  template: `
    @if (root(); as node) {
      <tem-node-host [node]="node" [form]="form()" [mode]="mode()" />
    }
  `,
  styles: [`
    :host {
      display: block;
      background: var(--tem-bg);
      color: var(--tem-ink);
      font-family: var(--tem-font-family);
    }
  `]
})
export class ScreenRendererComponent {

  readonly document = input.required<ScreenDocument>();
  readonly mode = input<RenderMode>('view');

  /**
   * O formulário vem de FORA, montado por `ScreenFormService`.
   *
   * O renderer não monta o próprio formulário de propósito: quem exibe a tela
   * também precisa ler os valores, validar antes de gravar e reagir a eles. Se o
   * formulário nascesse aqui dentro, o dono da tela teria de pescá-lo do
   * componente — que é como o canvas antigo acabou varrendo `__ngContext__`.
   */
  readonly form = input<FormGroup | null>(null);

  /**
   * O dado que alimentou a tela.
   *
   * Repassado ao `ScreenContext` para quem precisar dele além do próprio nó —
   * hoje o cartão de dashboard no modo `dataPath`. Não vai para o `NodeHost`:
   * as entradas de um renderer continuam sendo as mesmas três.
   */
  readonly data = input<unknown>(null);

  readonly valueChange = output<ScreenValueChange>();

  private readonly context = inject(ScreenContext);

  constructor() {
    // O contexto acompanha o documento. Renderer que precise de mais do que o
    // proprio no (a grade, para achar os paineis de mestre-detalhe) le daqui,
    // em vez de o NodeHost passar entradas diferentes por tipo.
    effect(() => {
      this.context.setDocument(this.document());
      this.context.setData(this.data());
    });
  }

  readonly root = computed<ScreenNode | null>(() => {
    const doc = this.document();
    if (doc === null || doc === undefined) {
      return null;
    }
    return doc.root ?? null;
  });
}
